<template>
  <div
    class="ext-card"
    :class="{ 'is-recommended': recommended }"
  >
    <div class="ext-top">
      <div>
        <div class="ext-name">
          {{ courseName }}
          <span
            v-if="recommended"
            class="ext-rec-badge"
          >
            おすすめ
          </span>
        </div>
        <div class="ext-meta">
          <template v-if="durationLabel">
            {{ durationLabel }} ｜
          </template>
          {{ candidate.courseHistoryTemp.size }}名
        </div>
      </div>

      <div class="ext-price-area">
        <v-progress-circular
          v-if="isCalculating"
          indeterminate
          size="16"
          width="2"
        />
        <template v-else-if="estimatedTotal !== null && estimatedTotal !== undefined">
          <div class="ext-price">
            ¥{{ Number(estimatedTotal).toLocaleString() }}
          </div>
          <div class="ext-price-sub">
            注文後のお会計
          </div>
        </template>
      </div>
    </div>

    <div class="ext-lines">
      <div
        v-for="(line, lineIdx) in lines"
        :key="`line-${lineIdx}`"
        class="ext-line"
      >
        <div class="ext-line-main">
          <span class="ext-line-name">{{ line.name }}</span>
          <span class="ext-line-calc">
            ¥{{ Number(line.temp.value.sellingPrice || 0).toLocaleString() }}
            <template v-if="line.temp.size > 1">
              × {{ line.temp.size }}
            </template>
          </span>
          <span class="ext-line-sub">
            ¥{{ ((line.temp.value.sellingPrice || 0) * line.temp.size).toLocaleString() }}
          </span>
        </div>

        <v-btn-toggle
          :value="line.temp.value.taxCharge"
          mandatory
          dense
          class="ext-tax-toggle"
          :aria-label="`${line.name}の料金区分`"
          @change="line.temp.value.taxCharge = $event"
        >
          <v-btn
            v-for="taxOption in taxOptions"
            :key="taxOption.value"
            :value="taxOption.value"
            x-small
            :ripple="false"
            :aria-label="taxOption.label"
          >
            {{ taxOption.label }}
          </v-btn>
        </v-btn-toggle>
      </div>
    </div>

    <v-btn
      block
      color="primary"
      class="ext-submit-btn mt-3"
      :ripple="false"
      :loading="submitting"
      :disabled="submitting || disabled"
      aria-label="延長する"
      @click="$emit('submit')"
    >
      延長する
    </v-btn>
  </div>
</template>

<script>
import { computed, inject } from '@vue/composition-api'
import { find } from 'lodash'

const findVendible = (vendibles, vendibleId) => {
  return find(vendibles, vendible => Number(vendible.id) === Number(vendibleId))
}

export default {
  props: {
    candidate: {
      type: Object,
      required: true,
    },
    estimatedTotal: {
      type: [Number, String],
      default: null,
    },
    isCalculating: {
      type: Boolean,
      default: false,
    },
    recommended: {
      type: Boolean,
      default: false,
    },
    submitting: {
      type: Boolean,
      default: false,
    },
    // 自動延長の処理中など、送信自体を受け付けない状態
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const courses = inject('courses', [])
    const nominations = inject('nominations', [])
    const items = inject('items', [])

    const targetCourse = computed(() => {
      return findVendible(courses.value, props.candidate.courseHistoryTemp.value.vendibleId)
    })

    const courseName = computed(() => {
      return targetCourse.value?.attributes?.name || '延長セット'
    })

    const durationLabel = computed(() => {
      const durationSeconds = targetCourse.value?.attributes?.effectiveDurationSeconds
      if (!durationSeconds) return ''

      return `${Math.round(durationSeconds / 60)}分`
    })

    const lines = computed(() => {
      const { candidate } = props

      return [
        {
          temp: candidate.courseHistoryTemp,
          name: courseName.value,
        },
        ...candidate.nominationHistoriesTemp.map(temp => ({
          temp,
          name: findVendible(nominations.value, temp.value.vendibleId)?.attributes?.name || '指名',
        })),
        ...candidate.itemHistoriesTemp.map(temp => ({
          temp,
          name: findVendible(items.value, temp.value.vendibleId)?.attributes?.name || 'アイテム',
        })),
      ]
    })

    return {
      courseName,
      durationLabel,
      lines,
      taxOptions: [
        { value: 'normal', label: '通常' },
        { value: 'included', label: '込み' },
        { value: 'cut', label: 'カット' },
      ],
    }
  },
}
</script>

<style lang="scss" scoped>
.ext-card {
  background: var(--overview-surface-2, #18181f);
  border: 1px solid var(--overview-border, rgba(255, 255, 255, 0.07));
  border-radius: 16px;
  padding: 14px;
  margin-bottom: 12px;

  &.is-recommended {
    border-color: var(--overview-accent-soft, rgba(139, 92, 246, 0.45));
  }
}

.ext-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 10px;
}

.ext-name {
  font-size: 15px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 6px;
}

.ext-rec-badge {
  font-size: 10px;
  font-weight: 700;
  padding: 1px 7px;
  border-radius: 999px;
  background: var(--overview-accent-softer, rgba(139, 92, 246, 0.14));
  color: var(--overview-accent, #8b5cf6);
}

.ext-meta {
  font-size: 12px;
  opacity: 0.6;
  margin-top: 2px;
}

.ext-price-area {
  text-align: right;
}

.ext-price {
  font-size: 17px;
  font-weight: 800;
}

.ext-price-sub {
  font-size: 10px;
  opacity: 0.55;
}

.ext-line {
  padding: 8px 0;
  border-top: 1px solid var(--overview-border, rgba(255, 255, 255, 0.07));
}

.ext-line-main {
  display: flex;
  align-items: center;
  font-size: 13px;
  margin-bottom: 6px;
}

.ext-line-name {
  flex: 1;
  min-width: 0;
  font-weight: 600;
}

.ext-line-calc {
  opacity: 0.6;
  margin-right: 10px;
  font-size: 12px;
}

.ext-line-sub {
  font-weight: 700;
}

.ext-tax-toggle {
  background: transparent !important;
}
</style>
